import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";
import { web3 } from "@project-serum/anchor";
import { Token2022Client } from "@/api/token2022/token_2022";
import { FluxBeamClient } from "@/api/fluxbeam/fluxBeamClient";
import { RugCheckClient } from "@/api/rugcheck/client";
import Client from "@/api/token_swap/client";
import { Commitment, ConnectionConfig } from "@solana/web3.js";
import {
	ISettings,
	IToken,
	METADATA_2022_PROGRAM_ID,
	TOKEN22_PROGRAM_ID,
	processTokenByExtension,
	unpackMint,
} from "@/shared";
import { PROGRAM_ID } from "@metaplex-foundation/mpl-token-metadata";
import { JupiterClient } from "@/api/jupiter/client";
import { getExtensionTypes } from "@solana/spl-token";
import { useCacheStore } from "@/pinia/cacheStore";

// interfaces
interface IPriorityFees {
	open: number;
	close: number;
	high: number;
	low: number;
	average: number;
	pct75: number;
	pct25: number;
}

export const useSolanaStore = defineStore("solana", () => {
	//store
	const cacheStore = useCacheStore();

	// data
	const endpoint = ref<string>("");
	const cluster = ref<string>("mainnet-beta");
	const commitment = ref<Commitment | ConnectionConfig>("processed");
	const client = ref<web3.Connection | null>(null);
	const tokenClient = ref<Token2022Client | null>(null);
	const fluxClient = ref<FluxBeamClient>(new FluxBeamClient());
	const rugCheckClient = ref<RugCheckClient>(new RugCheckClient());
	const swapClient = ref<Client | null>(null);
	const jitoClient = ref<web3.Connection | null>(null);
	const jupiterClient = ref<JupiterClient | null>(null);
	const priorityFees = reactive<IPriorityFees>({
		open: 1,
		close: 1,
		high: 1,
		low: 1,
		average: 1,
		pct75: 1,
		pct25: 1,
	});
	const validatorTips = reactive<IPriorityFees>({
		open: 1,
		close: 1,
		high: 1,
		low: 1,
		average: 1,
		pct75: 1,
		pct25: 1,
	});
	const feeChannel = new BroadcastChannel("fees:update");
	const tipChannel = new BroadcastChannel("tips:update");

	// methods
	function init(url: string): void {
		endpoint.value = url;

		client.value = new web3.Connection(endpoint.value, commitment.value);

		if (!client.value) return;

		jitoClient.value = new web3.Connection(
			"https://mainnet.block-engine.jito.wtf/api/v1/transactions",
			"processed"
		);
		tokenClient.value = new Token2022Client(client.value as web3.Connection);
		swapClient.value = new Client(client.value as web3.Connection);
		jupiterClient.value = new JupiterClient(client.value);
		cluster.value = endpoint.value.includes("devnet")
			? "devnet"
			: "mainnet-beta";
	}
	async function fetchClusterFees(): Promise<void> {
		feeChannel.onmessage = ({ data }) => {
			Object.assign(priorityFees, data);
		};
		tipChannel.onmessage = ({ data }) => {
			Object.assign(validatorTips, data);
		};
		// TODO: add the implementation below after delete old solana store
		// const fees = await fluxClient.value.getPriorityFees();

		// if (fees) {
		// 	Object.assign(priorityFees, fees);
		// }

		// setTimeout(() => {
		// 	fetchClusterFees();
		// }, 5000);
	}
	async function getTokenOnChain(mint: string): Promise<IToken | undefined> {
		const pubkey = new web3.PublicKey(mint);
		const data = await tokenClient.value.connection.getAccountInfo(
			pubkey,
			"processed"
		);

		if (!data || data.lamports === 0) {
			return;
		}

		const token = unpackMint(mint, data, data.owner);
		token.extensions = getExtensionTypes(token.tlvData);
		Object.assign(token, processTokenByExtension(token));
		return token
	}
	async function getToken(mint: string): Promise<IToken | undefined> {
		const tokenData = await (
			await fetch(`https://api.degencdn.com/v1/tokens/${mint}`)
		).json();

		const token = {
			address: new web3.PublicKey(mint),
			amount: 0,
			decimals: tokenData.decimals,
			mint: mint,
			name: format(tokenData?.name || ""),
			symbol: format(tokenData?.symbol || ""),
			image: tokenData.imageUri
		} as IToken

		cacheStore.addTokenList(token);

		if (token.symbol) {
			cacheStore.addTokenName(mint, token.symbol);
		}

		return token;
	}

	function getPriorityFees(settings: ISettings): number {
		const { pct75 } = priorityFees;

		const feesByLevel = {
			market: [pct75, 0.0075],
			high: [pct75 * 5, 0.075],
			turbo: [pct75 * 10, 0.1],
		};

		let [fee, max] = feesByLevel[settings.priorityLevel];

		max = max * Math.pow(10, 9);

		let customMaxFee = max;

		if (settings.priorityFeeMax) {
			customMaxFee = settings.priorityFeeMax * Math.pow(10, 9);
		}

		fee = Math.min(fee, max);

		if (customMaxFee !== 0) {
			fee = Math.min(fee, customMaxFee);
			return fee;
		}

		if (fee === 0) {
			fee = 0.00002 * Math.pow(10, 9); //Sane default
		}

		return fee * 1_000_000;
	}

	// getters
	const getFees = computed<IPriorityFees>(() => {
		return priorityFees;
	});
	const getTips = computed<IPriorityFees>(() => {
		return validatorTips;
	});

	function format(text: string): string {
		return text
			.trim()
			.split(/\r|\r\n|\n/)[0]
			.replace(/\0/g, "")
			.toUpperCase();
	}

	return {
		tokenClient,
		client,
		swapClient,
		jupiterClient,
		rugCheckClient,
		fluxClient,
		init,
		fetchClusterFees,
		getToken,
		getTokenOnChain,
		getPriorityFees,
		getFees,
		getTips,
	};
});
